import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, Zap, Building2, Rocket } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "axios";
import { API } from "../App";

const Pricing = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setTiers(response.data.tiers);
      } catch (error) {
        // Fallback pricing
        setTiers([
          {
            id: "free",
            name: "Free",
            price: 0,
            features: [
              "3 comparisons per month",
              "Up to 3 vendors per comparison",
              "Basic line item matching",
              "CSV export"
            ]
          },
          {
            id: "standard",
            name: "Standard",
            price: 29,
            features: [
              "20 comparisons per month",
              "Up to 10 vendors per comparison",
              "AI-powered analysis",
              "Pros & cons analysis",
              "Excel & CSV export"
            ]
          },
          {
            id: "pro",
            name: "Pro",
            price: 79,
            features: [
              "Unlimited comparisons",
              "Unlimited vendors",
              "Advanced AI analysis",
              "Recommendation scores",
              "Priority support",
              "API access"
            ]
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const tierIcons = {
    free: <Zap className="w-8 h-8" />,
    standard: <Building2 className="w-8 h-8" />,
    pro: <Rocket className="w-8 h-8" />
  };

  const tierColors = {
    free: "border-gray-500/30 hover:border-gray-400",
    standard: "border-primary-main/50 hover:border-primary-main ring-1 ring-primary-main/20",
    pro: "border-secondary-main/50 hover:border-secondary-main"
  };

  return (
    <div className="min-h-screen bg-[#020408] grid-bg">
      <Navbar />
      
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-primary-main uppercase tracking-widest text-sm">Pricing</span>
            <h1 className="font-display text-5xl md:text-6xl font-bold mt-4 tracking-tighter">
              Choose Your <span className="text-primary-main">Plan</span>
            </h1>
            <p className="text-xl text-gray-400 mt-6 max-w-2xl mx-auto">
              Start free and scale as your needs grow. All plans include our core comparison features.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="animate-pulse-glow w-16 h-16 rounded-full border-2 border-primary-main" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {tiers.map((tier, index) => (
                <div 
                  key={tier.id}
                  data-testid={`pricing-tier-${tier.id}`}
                  className={`relative glass rounded-xl p-8 transition-all duration-300 ${tierColors[tier.id]}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {tier.id === "standard" && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-primary-main text-black text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                        Popular
                      </span>
                    </div>
                  )}
                  
                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 ${
                    tier.id === "standard" ? "bg-primary-main/20 text-primary-main" :
                    tier.id === "pro" ? "bg-secondary-main/20 text-secondary-main" :
                    "bg-gray-500/20 text-gray-400"
                  }`}>
                    {tierIcons[tier.id]}
                  </div>
                  
                  <h3 className="font-display text-2xl font-semibold mb-2">{tier.name}</h3>
                  
                  <div className="mb-6">
                    <span className="font-display text-5xl font-bold">
                      ${tier.price}
                    </span>
                    <span className="text-gray-400 ml-2">/month</span>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          tier.id === "standard" ? "text-primary-main" :
                          tier.id === "pro" ? "text-secondary-main" :
                          "text-gray-500"
                        }`} />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    data-testid={`select-tier-${tier.id}-btn`}
                    onClick={() => navigate("/signup", { state: { tier: tier.id } })}
                    className={`w-full btn-skew font-bold uppercase tracking-wider py-6 transition-all ${
                      tier.id === "standard" 
                        ? "bg-primary-main text-black hover:bg-primary-light hover:shadow-glow-cyan" 
                        : tier.id === "pro"
                        ? "bg-secondary-main text-black hover:bg-secondary-light hover:shadow-glow-fuchsia"
                        : "bg-gray-700 text-white hover:bg-gray-600"
                    }`}
                  >
                    <span>{tier.price === 0 ? "Get Started" : "Subscribe"}</span>
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* FAQ Section */}
          <div className="mt-24 max-w-3xl mx-auto">
            <h2 className="font-display text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {[
                { q: "Can I upgrade or downgrade anytime?", a: "Yes, you can change your plan at any time. Changes take effect immediately." },
                { q: "What file formats do you support?", a: "We support PDF, Excel (.xlsx, .xls), CSV, Word (.docx), and common image formats (PNG, JPG, WebP)." },
                { q: "How accurate is the AI analysis?", a: "Our AI uses advanced models to extract and analyze data with high accuracy. We recommend reviewing results for critical decisions." },
                { q: "Is my data secure?", a: "Yes, all data is encrypted in transit and at rest. We never share your vendor information with third parties." }
              ].map((faq, index) => (
                <div key={index} className="glass p-6 rounded-xl">
                  <h4 className="font-display text-lg font-semibold mb-2">{faq.q}</h4>
                  <p className="text-gray-400">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
